/**
 * Shared mailboxes — *skupni predal* (SPEC §1.1H).
 *
 * A couple where one signed up both, a parent whose address is the contact for
 * their children: several volunteer rows, one inbox. Each row stays its own
 * person — own token, own briefing, own confirmation — and what they share is
 * the address: one mail per mailbox per send, and a mail arriving from that
 * address lands in every one of their threads.
 *
 * Nothing is stored for it. Two rows are a household exactly while their
 * normalised addresses are equal, so correcting a typo in one address splits
 * them and giving two people one address joins them. The address is the only
 * fact the signup sheet gives — the parent in the parent–child case is not a
 * volunteer row that could carry a relation — so a derived group is the one
 * that never goes stale.
 *
 * Like {@link isExternal}, this is the one spelling of the question. The inbox
 * matcher, the batch grouping, the import notice and the profile chip all ask
 * through here, so they cannot disagree on who shares with whom.
 */

import { isExternal } from './constants';

/** Trimmed and lowercased; `''` for nothing. What two spellings of one address share. */
export function normalizeEmail(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}

export interface MailboxRow {
  email: string | null;
  active: boolean;
  externalGroupId: string | null;
  /** GDPR soft-delete stamp. A blanked row keeps its id but reads no mail. */
  anonymisedAt?: Date | string | null;
}

/**
 * What two rows share when they read one inbox, or null when this row can be
 * in no household: no address, deactivated, anonymised, or a place in an
 * external team — its leader is written to by hand, never by the app.
 */
export function mailboxKey(v: MailboxRow): string | null {
  if (!v.active || isExternal(v) || (v.anonymisedAt ?? null) !== null) return null;
  const key = normalizeEmail(v.email);
  return key === '' ? null : key;
}

export interface MailboxPerson extends MailboxRow {
  id: string;
  name: string;
}

/**
 * The order people take inside one mail, one report row, one thread marker:
 * by name, as the roster lists them. One order everywhere, so the household a
 * coordinator approves in a dialog is the household the mail then spells out.
 */
export function compareByName<T extends { name: string }>(a: T, b: T): number {
  return a.name.localeCompare(b.name, 'sl');
}

/** The others who read this person's inbox, by name — empty when nobody does. */
export function mailboxSiblings<T extends MailboxPerson>(v: T, roster: readonly T[]): T[] {
  const key = mailboxKey(v);
  if (key === null) return [];
  return roster.filter((o) => o.id !== v.id && mailboxKey(o) === key).sort(compareByName);
}

/**
 * The people one mail to this person is addressed to: themselves and whoever
 * shares their inbox, by name. Just `[v]` for an ordinary address.
 */
export function mailboxPeople<T extends MailboxPerson>(v: T, roster: readonly T[]): T[] {
  return [v, ...mailboxSiblings(v, roster)].sort(compareByName);
}

/**
 * Who in a selection is mailed together: the people on one shared address who
 * read the same language — a mail has one language, so a household that reads
 * two is two units. The unit's key is the address, suffixed by the language
 * only where the address alone would mix them. Nobody outside the selection
 * is pulled in: a bulk send mails exactly who was picked. People mailed alone
 * are absent from the result.
 */
export function mailboxUnits<T extends MailboxRow & { id: string; lang: string }>(
  rows: readonly T[],
): Map<string, string> {
  const byAddress = new Map<string, T[]>();
  for (const v of rows) {
    const key = mailboxKey(v);
    if (key === null) continue;
    byAddress.set(key, [...(byAddress.get(key) ?? []), v]);
  }
  const unitOf = new Map<string, string>();
  for (const [key, people] of byAddress) {
    const units = [...new Set(people.map((p) => p.lang))]
      .map((lang) => people.filter((p) => p.lang === lang))
      .filter((unit) => unit.length > 1);
    for (const unit of units) {
      const unitKey = units.length > 1 ? `${key}#${unit[0]!.lang}` : key;
      for (const p of unit) unitOf.set(p.id, unitKey);
    }
  }
  return unitOf;
}

/**
 * Every shared mailbox in a roster: key → its people, by name. Addresses read
 * by one person are left out, so the result is exactly the set of households.
 */
export function mailboxGroups<T extends MailboxPerson>(roster: readonly T[]): Map<string, T[]> {
  const byKey = new Map<string, T[]>();
  for (const v of roster) {
    const key = mailboxKey(v);
    if (key === null) continue;
    byKey.set(key, [...(byKey.get(key) ?? []), v]);
  }
  const out = new Map<string, T[]>();
  for (const [key, people] of byKey) {
    if (people.length > 1) out.set(key, people.sort(compareByName));
  }
  return out;
}
